import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { fetchModelSearch, type ModelSearchRow } from './api';
import { useDebouncedValue } from './useDebouncedValue';

function closeModelShell(): void {
  const modal = document.getElementById('rnm-model-modal');
  if (modal) {
    modal.style.display = 'none';
  }
  document.body.style.overflow = '';
  window.dispatchEvent(new CustomEvent('rnm-model-modal-closed'));
}

export function ModelSearchMount(): ReactElement {
  const [raw, setRaw] = useState('');
  const debounced = useDebouncedValue(raw.trim(), 300);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const enabled = debounced.length >= 3;

  const query = useQuery({
    queryKey: ['rnm', 'models', debounced],
    queryFn: () => fetchModelSearch(debounced),
    enabled,
  });

  const rows: ModelSearchRow[] = query.data ?? [];

  useEffect(() => {
    function onOpen(): void {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    function onClosed(): void {
      setRaw('');
    }
    window.addEventListener('rnm-model-modal-opened', onOpen);
    window.addEventListener('rnm-model-modal-closed', onClosed);
    return () => {
      window.removeEventListener('rnm-model-modal-opened', onOpen);
      window.removeEventListener('rnm-model-modal-closed', onClosed);
    };
  }, []);

  const setActiveFocus = useCallback((index: number) => {
    const items = listRef.current?.querySelectorAll('.rnm-result-item');
    if (!items) {
      return;
    }
    items.forEach((el) => el.classList.remove('active'));
    if (index >= 0 && index < items.length) {
      items[index].classList.add('active');
      items[index].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, []);

  const getActiveIndex = useCallback((): number => {
    const items = listRef.current?.querySelectorAll('.rnm-result-item');
    if (!items) {
      return -1;
    }
    for (let i = 0; i < items.length; i++) {
      if (items[i].classList.contains('active')) {
        return i;
      }
    }
    return -1;
  }, []);

  const go = useCallback((url: string) => {
    window.location.href = url;
  }, []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const items = listRef.current?.querySelectorAll('.rnm-result-item');
      if (!items || items.length === 0) {
        return;
      }
      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          const cur = getActiveIndex();
          const next = cur < 0 ? 0 : (cur + 1) % items.length;
          setActiveFocus(next);
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const cur = getActiveIndex();
          const next = cur < 0 ? items.length - 1 : (cur - 1 + items.length) % items.length;
          setActiveFocus(next);
          break;
        }
        case 'Enter': {
          e.preventDefault();
          const active = listRef.current?.querySelector(
            '.rnm-result-item.active'
          ) as HTMLElement | null;
          if (active) {
            const url = active.getAttribute('data-url');
            if (url) {
              go(url);
            }
            break;
          }
          const all = listRef.current?.querySelectorAll('.rnm-result-item');
          if (all && all.length === 1) {
            const url = (all[0] as HTMLElement).getAttribute('data-url');
            if (url) {
              go(url);
            }
          }
          break;
        }
        default:
          break;
      }
    },
    [getActiveIndex, go, setActiveFocus]
  );

  useEffect(() => {
    const items = listRef.current?.querySelectorAll('.rnm-result-item');
    if (!items) {
      return;
    }
    items.forEach((el) => el.classList.remove('active'));
  }, [debounced, rows.length]);

  let inner: ReactElement;
  if (query.isFetching) {
    inner = <div className="rnm-loading">Buscando...</div>;
  } else if (query.isError) {
    const msg =
      query.error instanceof Error && query.error.message === 'nonce_models'
        ? 'Sesión expirada. Recargando...'
        : 'Error al buscar. Intenta nuevamente.';
    inner = <div className="rnm-error">{msg}</div>;
  } else if (rows.length === 0) {
    inner = <div className="rnm-no-results">No se encontraron modelos</div>;
  } else {
    inner = (
      <ul className="rnm-results-list" ref={listRef}>
        {rows.map((result, index) => (
          <li
            key={result.id}
            className="rnm-result-item"
            data-index={index}
            data-url={result.url}
            role="option"
            onClick={() => go(result.url)}
            onTouchEnd={(ev) => {
              ev.preventDefault();
              go(result.url);
            }}
          >
            <span className="rnm-result-name">{result.name}</span>
            {result.count > 0 ? (
              <span className="rnm-result-count">({result.count})</span>
            ) : null}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <>
      <button
        type="button"
        className="rnm-modal-close"
        aria-label="Cerrar modal"
        onClick={closeModelShell}
      >
        ×
      </button>
      <h2 className="rnm-modal-title">Selecciona un modelo</h2>
      <p className="rnm-modal-caption">
        Si nos indicas tu vehículo, la navegación será más sencilla y verás principalmente los
        productos que necesitas
      </p>
      <div className="rnm-modal-search">
        <input
          ref={inputRef}
          type="text"
          id="rnm-model-search"
          className="rnm-search-input"
          placeholder="Buscar modelo de vehículo..."
          autoComplete="off"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <div id="rnm-search-results" className="rnm-search-results">
          {!enabled ? <div className="rnm-search-placeholder" /> : inner}
        </div>
      </div>
    </>
  );
}
